import {
  ChartPaletteColorOptions,
  HTMLColorOptions,
  Palette,
  SimplePaletteColorOptions,
  TypeBackground,
} from '@mui/material';
import {
  blueGrey,
  deepOrange,
  green,
  indigo,
  pink,
} from '@mui/material/colors';
import createPalette, { TypeText } from '@mui/material/styles/createPalette';

// Throwaway palette instance used purely to access `augmentColor` — MUI's
// helper that fills in `light`, `dark`, and `contrastText` from a single
// `main` color (same logic the theme runs internally when you pass `{ main }`).
// `datasets`/`html` are required by this project's PaletteOptions augmentation
// but unused here — we only call `augmentColor` off the result.
export const tonalPalette = createPalette({
  mode: 'light',
  datasets: {} as ChartPaletteColorOptions,
  html: {} as HTMLColorOptions,
});

// Generate a full SimplePaletteColorOptions from a single main color.
export const fromMain = (main: string): SimplePaletteColorOptions =>
  tonalPalette.augmentColor({ color: { main } });

// ! To clearly show the developers that we are not using some variants.
export const DONT_USE_COLOR: SimplePaletteColorOptions = {
  main: '#000',
  dark: '#000',
  light: '#000',
  contrastText: '#000',
};

// Shared chart dataset colors — used by every palette via the factory below.
const datasets: ChartPaletteColorOptions = {
  line: indigo[400],
  line_secondary: deepOrange[400],
  stepped: pink[400],
  complimentary_bar: blueGrey[400],
  scatter: green[400],
  scatter_secondary: deepOrange[400],
};

// Shared named-HTML color tokens — used by every palette via the factory below.
export const HTML: HTMLColorOptions = {
  crimson: '#DC143C',
  dark_green: '#006400',
  dodger_blue: '#1E90FF',
  gold: '#FFD700',
  white: '#FFFFFF',
  white_smoke: '#F5F5F5',
  black: '#000000',
  dark_gray: '#A9A9A9',
  dark_slate_gray: '#2F4F4F',
};

// Args accepted by the palette factory. Color slots take a single `main` —
// the factory expands each into a full SimplePaletteColorOptions via `fromMain`.
type PaletteArgs = {
  primary: string;
  secondary: string;
  text: TypeText;
  background: TypeBackground;
  success: string;
  warning: string;
  error: string;
  divider: string;
};

// Factory: build a full MUI Palette from a small set of main colors plus
// text/background. Datasets, HTML tokens, and the unused `info` slot are
// filled in from the shared defaults above so callers stay terse.
export const createThemePalette = ({
  primary,
  secondary,
  text,
  background,
  success,
  warning,
  error,
  divider,
}: PaletteArgs): Palette =>
  createPalette({
    mode: 'light',
    datasets,
    html: HTML,
    primary: fromMain(primary),
    secondary: fromMain(secondary),
    error: fromMain(error),
    success: fromMain(success),
    divider,
    warning: fromMain(warning),
    text,
    background,
    info: DONT_USE_COLOR,
  });
